import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { fetchSsndetails, postMember } from '../../../actions/member/addMemberActionCreators';
import ConditionalRendering from '../../../components/common/conditional-rendering/ConditionalRendering';
import ValidationErrorMessage from '../../../components/common/form/errorMessage';
import ViewMemberAccountModal from '../../../components/common/modals/ViewMemberAccountModal';
import { configs } from '../../../constants/configs/configs';
import { INVALID_CONSUMER, INVALID_EMAIL } from '../../../constants/members/members.constants';
import { useAppDispatch, useAppSelector, useDisclosure } from '../../../hooks';
import { ssnDetailsSelector } from '../../../selectors/memberPortalSelector';
import { formatDateToMMDDYYYY } from '../../../utils/dates/dateMethods';
import { DefaultValues, IFormInput, userRegistrationSchema } from '../common/addMemberFormConfig';
import { IAddMemberResponse } from '../common/types';
import AddMemberForm from './AddMemberForm';

const StyledInfoHeader = styled.h4`
    margin-bottom: 40px;
    background-color: #eeeef4;
    padding: 15px;
`;

const StyledError = styled.div`
    padding-left: 20px;

    .shown.error--msg {
        margin-top: 0px;
    }
`;

const AddMemberFormContainer: React.FC = () => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const [memberExist, setMemberExist] = useState(false);
    const [emailExist, setEmailExist] = useState(false);
    const [showSsnRequiredField, setShowSsnRequiredField] = useState(false);
    const ssnDetails = useAppSelector(ssnDetailsSelector);
    const [memberData, setMemberData] = useState<IAddMemberResponse>();

    const {
        isOpen: isOpenViewMemberAccountModal,
        onOpen: openViewMemberAccountModal,
        onClose: closeViewMemberAccountModal,
    } = useDisclosure();

    const methods = useForm<IFormInput>({
        defaultValues: DefaultValues,
        resolver: zodResolver(userRegistrationSchema(intl)),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    const watchedValues = useWatch({ control: methods.control });

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        methods.setValue('uniqueCallId', params.get('callID') || '');
        methods.setValue('recordId', params.get('recordId') || '');
        dispatch(fetchSsndetails(configs.endpoints.GET_SSN_DETAILS));
    }, []);

    useEffect(() => {
        if (ssnDetails?.status !== undefined) {
            setShowSsnRequiredField(ssnDetails?.data?.ssnRequired);
        }
    }, [ssnDetails]);

    useEffect(() => {
        if (memberExist) setMemberExist(false);
        if (emailExist) setEmailExist(false);
    }, [watchedValues]);

    const onSubmit: SubmitHandler<IFormInput> = async data => {
        setEmailExist(false);
        const payload: any = {
            firstName: data.firstName,
            lastName: data.lastName,
            dob: formatDateToMMDDYYYY(data.dob),
            zipcode: data.zipcode,
            phoneNumber: data.phoneNumber,
            ssnRequired: data.ssnRequired,
            callPermission: data.callPermission,
            uniqueCallId: data.uniqueCallId,
            recordId: data.recordId,
        };

        if (data.ssnNotReqdReason) {
            payload.ssnNotReqdReasons = data.ssnNotReqdReason;
        }
        if (data.emailAddress) {
            payload.emailAddress = data.emailAddress;
        }

        const result = await dispatch(postMember(configs.endpoints.ADD_MEMBER, payload));
        if (result == null) {
            alert(' Something went wrong.. please contact admin. ');
            return;
        }
        if (result?.status) {
            setMemberData(result);
            openViewMemberAccountModal();
        } else {
            const message = result?.message || '';
            if (message.includes(INVALID_CONSUMER)) setMemberExist(true);
            if (message.includes(INVALID_EMAIL)) setEmailExist(true);
        }
    };

    const onCancel = () => {
        closeViewMemberAccountModal();
        methods.reset();
    };

    const switchToNonDefaultURL = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const encssapApplicationId = params.get('appId');
        let switchToNonDefaultURL = '';
        if (encssapApplicationId) {
            switchToNonDefaultURL = `/referral/csrflow/${encssapApplicationId}/${memberData?.householdId}`;
        }
        return switchToNonDefaultURL;
    }, [memberData?.householdId]);

    return (
        <>
            <StyledInfoHeader>
                <FormattedMessage id="member.add_member.information" />
            </StyledInfoHeader>
            <StyledError>
                <ConditionalRendering isTrue={memberExist}>
                    <ValidationErrorMessage
                        isVisible={memberExist}
                        messageText="member.duplicate.record.validation.message"
                    />
                </ConditionalRendering>
            </StyledError>
            <AddMemberForm
                methods={methods}
                onSubmit={onSubmit}
                emailExist={emailExist}
                ssnRequired={showSsnRequiredField}
            />
            {isOpenViewMemberAccountModal && memberData && (
                <ViewMemberAccountModal
                    encHHId={memberData.householdId}
                    hhId={memberData.unencryptedHouseholdId}
                    hhFirstName={memberData.firstName}
                    hhLastName={memberData.lastName}
                    viewType="Individual"
                    primaryBtnLabel="common.individual_view"
                    secondaryBtnLabel="member.add_member.button.cancel"
                    title="modals.header.view_individual_account"
                    onClose={onCancel}
                    switchToModuleName={'individual'}
                    switchToNonDefaultURL={switchToNonDefaultURL}
                    action="enroll"
                />
            )}
        </>
    );
};

export default AddMemberFormContainer;
